/**
 * No Relief state for the current session: the live points (from the roster),
 * merged with the shared call log (from the server).
 *
 * The points recompute on every roster change, so the list follows edits made
 * here or by anyone else instantly. The log is re-fetched whenever another
 * user ticks (or undoes a tick on) something (`liveUpdateTick`, bumped by the
 * no_relief_update websocket message).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '@/api/client';
import type { NoReliefAck, NoReliefAckKind } from '@/types/api';
import {
  buildNoReliefGroups, computeNoRelief, countPendingCalls,
  type NoReliefTrackingState,
} from '@/utils/noRelief';

export function useNoRelief(
  counterMatrix: string[][][] | undefined,
  sessionId: string | undefined,
  liveUpdateTick: number,
) {
  const [acks, setAcks] = useState<NoReliefAck[]>([]);

  useEffect(() => {
    if (!sessionId || !counterMatrix) return;
    let cancelled = false;
    apiClient.noRelief.listAcks()
      .then((list) => { if (!cancelled) setAcks(list); })
      .catch(() => { /* keep the last known log — the roster half still works */ });
    return () => { cancelled = true; };
    // counterMatrix only gates the first fetch (no roster → nothing to track);
    // re-fetching on every edit would be wasted, the log doesn't change with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, liveUpdateTick, !!counterMatrix]);

  // Carries buildNoReliefGroups' tracking state across renders, so a live
  // round trip through the opposite kind (nobody called about it) is still
  // caught as owed even once it lands back on the last ticked kind — see
  // noRelief.ts's own doc comment. Reset whenever the session changes: the
  // memory is meaningless against a different roster.
  const trackingRef = useRef<NoReliefTrackingState>(new Map());
  const trackingSessionRef = useRef(sessionId);
  if (trackingSessionRef.current !== sessionId) {
    trackingSessionRef.current = sessionId;
    trackingRef.current = new Map();
  }

  const groups = useMemo(() => {
    const { groups: g, state } = buildNoReliefGroups(
      counterMatrix ? computeNoRelief(counterMatrix) : [],
      acks,
      trackingRef.current,
    );
    trackingRef.current = state;
    return g;
  }, [counterMatrix, acks]);
  const pendingCount = useMemo(() => countPendingCalls(groups), [groups]);

  const addAck = useCallback(async (counter: number, slot: number, kind: NoReliefAckKind) => {
    const ack = await apiClient.noRelief.addAck(counter, slot, kind);
    setAcks((prev) => (prev.some((a) => a.id === ack.id) ? prev : [...prev, ack]));
  }, []);

  /** Undo a tick — pressing the same one again after ticking it by mistake.
   *  Only the latest tick for a counter/slot can be undone (see the backend);
   *  the server is the source of truth on whether this one qualifies. */
  const deleteAck = useCallback(async (ackId: number) => {
    await apiClient.noRelief.deleteAck(ackId);
    setAcks((prev) => prev.filter((a) => a.id !== ackId));
  }, []);

  return { groups, pendingCount, addAck, deleteAck };
}
